/**
 * 流し読みモード: AI が選んだ章の重要文に、本文の外側の余白へ傍線と印を付ける
 *
 * 文字の上に塗りや下線を重ねると、自分で付けるハイライト（塗り）や取り消し線と紛らわしく、
 * 縦書きでは隣の列にかかってしまう。そこで文の位置（行ごとの矩形）を測り、
 * 本文の上に重ねた透明な層へ「行の外側の傍線」と「文頭の小さな印（核・要）」を描く。
 * - 縦書き: 列の右側の余白に縦線 / 文頭の上に印
 * - 横書き: 行の下の余白に横線 / 文頭の左に印
 * 本文の DOM には触れないので、Readium の位置計算やハイライトには影響しない。
 *
 * 読む目的に関係する箇所（数段落のまとまり）には、行頭側の余白に紫の帯を付ける。
 * - 縦書き: 列の上の余白に横帯 / 横書き: 行の左の余白に縦帯（重要文の傍線とは反対側なので重ならない）
 * - 断片の途中にある図の文字（本文と向きが違う行・短く孤立した行）には帯を付けない
 */
import type { SkimLevel } from '@/store/useUISettingsStore'

/**
 * 重要度ごとの傍線の太さ・色と文頭の印（核心: 赤 / 重要: 黄 / 補足: 緑。補足は印なし）
 * 自分で付けるハイライトは文字の上の塗りなので、余白の線と印で見分けられる。
 * badgeText は印の文字色（黄の地に白は読みにくいので濃い色にする）
 */
export const SKIM_LEVEL_STYLES: Record<
  SkimLevel,
  { thickness: number; color: string; badge: string | null; badgeText: string }
> = {
  1: { thickness: 3, color: '#ef4444', badge: '核', badgeText: '#ffffff' },
  2: { thickness: 2, color: '#eab308', badge: '要', badgeText: '#422006' },
  3: { thickness: 2, color: '#22c55e', badge: null, badgeText: '#ffffff' },
}

export const SKIM_LEVEL_LABELS: Record<SkimLevel, string> = { 1: '核心', 2: '重要', 3: '補足' }

/** 読む目的に関係する箇所の帯の色（重要文の赤・黄・緑と見分ける） */
export const PURPOSE_MARK_COLOR = '#a855f7'
const PURPOSE_BAR_THICKNESS = 3
// 行頭から帯までの間（px）
const PURPOSE_BAR_GAP = 5
// 本文の断片の照合で、範囲が断片の長さのこの倍を超えたら末尾の一致を誤りとみなす
const PASSAGE_SPAN_RATIO = 1.5
// 帯の長さが文字のこの数より短いもの（図のラベルなど）は帯を付けない
const PASSAGE_MIN_BAND_CHARS = 4
// 文字数から推定した断片の端を、この文字数以内にある文の切れ目に合わせる
const SENTENCE_SNAP_CHARS = 80
const SENTENCE_END_CHARS = '。！？!?'
const CLOSING_CHARS = '」』）)】'

const OVERLAY_CLASS = 'pml-skim-overlay'
// 文字の外形から傍線までの間（px）
const LINE_GAP = 2
const BADGE_SIZE = 16
// 文字の大きさ・幅が変わったときに引き直すまでの待ち時間 (ms)
const REDRAW_DELAY_MS = 150
// 短すぎる文は本文の別の場所に誤って一致しやすいので強調しない
const MIN_SENTENCE_CHARS = 6
// 文全体が一致しないとき（改行・ルビの扱いの違いなど）に、文頭のこの文字数で位置を探す
const ANCHOR_CHARS = 16
// 縦長の矩形をこの比率で縦書きとみなす（PDF の文字レイヤーは writing-mode を持たないため）
const VERTICAL_ASPECT = 1.5
const SKIP_SELECTOR = 'rt, rp, script, style'
const WHITESPACE_RE = /\s/
const WHITESPACES_RE = /\s+/g

/** 強調する重要文と重要度 */
export type SkimSentence = { text: string; level: SkimLevel }

/** 本文に付ける印: 流し読みの重要文と、読む目的に関係する本文の断片 */
export type ReadingMarks = { sentences: SkimSentence[]; passages: string[] }

/** 空白とルビの読みを除いた本文と、その各文字がどのテキストノードの何文字目かの対応 */
type TextIndex = {
  text: string
  nodes: Text[]
  nodeOf: number[]
  offsetOf: number[]
}

const isTextNode = (node: Node): node is Text => node.nodeType === Node.TEXT_NODE

const buildTextIndex = (root: Node): TextIndex => {
  const doc = root.ownerDocument ?? (root as Document)
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const chars: string[] = []
  const nodes: Text[] = []
  const nodeOf: number[] = []
  const offsetOf: number[] = []

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!isTextNode(node) || node.parentElement?.closest(SKIP_SELECTOR)) continue
    const nodeIndex = nodes.push(node) - 1
    const value = node.data
    for (let i = 0; i < value.length; i++) {
      const ch = value.charAt(i)
      if (WHITESPACE_RE.test(ch)) continue
      chars.push(ch)
      nodeOf.push(nodeIndex)
      offsetOf.push(i)
    }
  }
  return { text: chars.join(''), nodes, nodeOf, offsetOf }
}

/** 本文の start〜end 文字目を、テキストノードごとの Range に分ける（ルビの読みは含まない） */
const toNodeRanges = (doc: Document, index: TextIndex, start: number, end: number): Range[] => {
  const ranges: Range[] = []
  let i = start
  while (i <= end) {
    const nodeIndex = index.nodeOf.at(i)
    const node = nodeIndex == null ? undefined : index.nodes.at(nodeIndex)
    const from = index.offsetOf.at(i)
    if (nodeIndex == null || !node || from == null) break
    let j = i
    while (j + 1 <= end && index.nodeOf.at(j + 1) === nodeIndex) j++
    const to = index.offsetOf.at(j) ?? from
    const range = doc.createRange()
    range.setStart(node, from)
    range.setEnd(node, to + 1)
    ranges.push(range)
    i = j + 1
  }
  return ranges
}

/**
 * 本文に現れそうな文だけに絞る（1 文字ずつの対応表づくりは重いので、どれも無い本文では作らない）
 *
 * textContent にはルビの読みが混ざり文頭で一致しなくなるため、ルビのある本文では絞らない。
 */
const narrowCandidates = <T extends { text: string }>(
  root: Node,
  sentences: T[],
  anchors: (text: string) => string[] = (text) => [text.slice(0, ANCHOR_CHARS)],
): T[] => {
  if (root instanceof Element && root.querySelector('rt')) return sentences
  const flat = (root.textContent ?? '').replace(WHITESPACES_RE, '')
  return sentences.filter((s) => anchors(s.text.replace(WHITESPACES_RE, '')).some((a) => flat.includes(a)))
}

/** root 配下の本文から、各文に一致する箇所を探す（1 文ごとに、テキストノード単位の Range の列） */
export const findSentenceMatches = <T extends { text: string }>(
  root: Node,
  allSentences: T[],
): { sentence: T; ranges: Range[] }[] => {
  const sentences = narrowCandidates(root, allSentences)
  if (sentences.length === 0) return []
  const index = buildTextIndex(root)
  if (!index.text) return []
  const doc = root.ownerDocument ?? (root as Document)

  const matches: { sentence: T; ranges: Range[] }[] = []
  for (const sentence of sentences) {
    const target = sentence.text.replace(WHITESPACES_RE, '')
    if (target.length < MIN_SENTENCE_CHARS) continue
    let start = index.text.indexOf(target)
    let length = target.length
    if (start < 0) {
      start = index.text.indexOf(target.slice(0, ANCHOR_CHARS))
      if (start < 0) continue
      length = Math.min(target.length, index.text.length - start)
    }
    const ranges = toNodeRanges(doc, index, start, start + length - 1)
    if (ranges.length > 0) matches.push({ sentence, ranges })
  }
  return matches
}

/**
 * root 配下の本文から、本文の断片（数段落）が現れる範囲を探す（1 断片ごとに、テキストノード単位の Range の列）
 *
 * 断片は長く、表記の揺れで全体が一致しないことがあるので、先頭と末尾の数文字で範囲を決める。
 * 断片が別の本文ファイルにまたがるときは、このファイルにある分だけを返す。
 */
export const findPassageMatches = (root: Node, passages: string[]): Range[][] => {
  const targets = passages.map((p) => p.replace(WHITESPACES_RE, '')).filter((t) => t.length >= MIN_SENTENCE_CHARS)
  const candidates = narrowCandidates(
    root,
    targets.map((text) => ({ text })),
    (text) => [text.slice(0, ANCHOR_CHARS), text.slice(-ANCHOR_CHARS)],
  )
  if (candidates.length === 0) return []
  const index = buildTextIndex(root)
  if (!index.text) return []
  const doc = root.ownerDocument ?? (root as Document)

  const intervals: [number, number][] = []
  for (const { text: target } of candidates) {
    const head = target.slice(0, ANCHOR_CHARS)
    const tail = target.slice(-ANCHOR_CHARS)
    let start = index.text.indexOf(head)
    let end: number
    if (start >= 0) {
      const tailAt = index.text.indexOf(tail, start)
      end =
        tailAt >= 0 && tailAt - start <= target.length * PASSAGE_SPAN_RATIO
          ? tailAt + tail.length - 1
          : sentenceEndFrom(index.text, Math.min(start + target.length, index.text.length) - 1)
    } else {
      // 先頭が前のファイルにある断片は、このファイルの末尾側の一致から遡る
      const tailAt = index.text.indexOf(tail)
      if (tailAt < 0) continue
      end = tailAt + tail.length - 1
      start = sentenceStartFrom(index.text, Math.max(0, end + 1 - target.length))
    }
    intervals.push([start, end])
  }

  // 断片が重なると、後の断片の始まりが文の途中に印として出てしまうので、重なる断片は 1 つにまとめる
  const merged: [number, number][] = []
  for (const [start, end] of intervals.sort((a, b) => a[0] - b[0])) {
    const last = merged.at(-1)
    if (last && start <= last[1] + 1) last[1] = Math.max(last[1], end)
    else merged.push([start, end])
  }
  return merged.map(([start, end]) => toNodeRanges(doc, index, start, end)).filter((ranges) => ranges.length > 0)
}

/**
 * 文字数から推定した断片の始まりを、直前の文の切れ目に合わせる（文の途中から帯が始まらないように）
 * 近くに文の切れ目が無ければ推定のまま
 */
const sentenceStartFrom = (text: string, start: number): number => {
  const from = Math.max(0, start - SENTENCE_SNAP_CHARS)
  for (let i = start - 1; i >= from; i--) {
    if (!SENTENCE_END_CHARS.includes(text.charAt(i))) continue
    let next = i + 1
    while (next < start && CLOSING_CHARS.includes(text.charAt(next))) next++
    return next
  }
  return from === 0 ? 0 : start
}

/** 文字数から推定した断片の終わりを、直後の文の切れ目に合わせる */
const sentenceEndFrom = (text: string, end: number): number => {
  const to = Math.min(text.length - 1, end + SENTENCE_SNAP_CHARS)
  for (let i = end; i <= to; i++) {
    if (!SENTENCE_END_CHARS.includes(text.charAt(i))) continue
    let last = i
    while (last + 1 <= to && CLOSING_CHARS.includes(text.charAt(last + 1))) last++
    return last
  }
  return to === text.length - 1 ? to : end
}

// ============================================================
// 描画
// ============================================================

/** 画面上の座標を、描画先の層の座標に直す */
type Origin = { x: number; y: number; scale: number }

type Rect = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>

/**
 * 矩形の形と並びから縦書きかを判断する（writing-mode を持たない PDF の文字レイヤー向け）
 * 複数文字の span なら縦長か横長かで分かる。1 文字ずつの span（ほぼ正方形）に分かれた PDF では、
 * 隣り合う文字が縦と横のどちらに並んでいるかで判断する
 */
export const isVerticalLayout = (rects: Rect[]): boolean => {
  const largest = rects.reduce<Rect | null>((a, b) => (!a || b.width * b.height > a.width * a.height ? b : a), null)
  if (!largest) return false
  if (largest.height > largest.width * VERTICAL_ASPECT) return true
  if (largest.width > largest.height * VERTICAL_ASPECT) return false
  let vertical = 0
  let horizontal = 0
  for (let i = 1; i < rects.length; i++) {
    const a = rects.at(i - 1)
    const b = rects.at(i)
    if (!a || !b) continue
    const dx = Math.abs((a.left + a.right - b.left - b.right) / 2)
    const dy = Math.abs((a.top + a.bottom - b.top - b.bottom) / 2)
    if (dy > dx) vertical++
    else if (dx > dy) horizontal++
  }
  return vertical > horizontal
}

const hasVerticalWritingMode = (ranges: Range[]): boolean => {
  const element = ranges.at(0)?.startContainer.parentElement
  const view = element?.ownerDocument.defaultView
  return !!element && !!view?.getComputedStyle(element).writingMode.startsWith('vertical')
}

const isVerticalText = (ranges: Range[], rects: DOMRect[]): boolean =>
  hasVerticalWritingMode(ranges) || isVerticalLayout(rects)

/**
 * 本文の順に並ぶ矩形のうち、同じ行（縦書きは列）で隣り合うものを 1 本にまとめる
 * （1 文字ずつの span に分かれた PDF でも、傍線が文字ごとに途切れないように）
 */
export const joinRuns = (rects: Rect[], vertical: boolean): Box[] => {
  const runs: Box[] = []
  for (const rect of rects) {
    const run = runs.at(-1)
    const continues =
      !!run &&
      (vertical
        ? Math.abs((run.left + run.right - rect.left - rect.right) / 2) <
            Math.max(run.right - run.left, rect.width) / 2 && Math.abs(rect.top - run.bottom) <= rect.width
        : Math.abs((run.top + run.bottom - rect.top - rect.bottom) / 2) <
            Math.max(run.bottom - run.top, rect.height) / 2 && Math.abs(rect.left - run.right) <= rect.height)
    if (!run || !continues) {
      runs.push({ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom })
      continue
    }
    run.left = Math.min(run.left, rect.left)
    run.top = Math.min(run.top, rect.top)
    run.right = Math.max(run.right, rect.right)
    run.bottom = Math.max(run.bottom, rect.bottom)
  }
  return runs
}

const createBox = (doc: Document, style: string): HTMLDivElement => {
  const box = doc.createElement('div')
  box.style.cssText = `position:absolute;${style}`
  return box
}

const drawSentence = (layer: HTMLElement, ranges: Range[], level: SkimLevel, origin: Origin): boolean => {
  const rects = ranges.flatMap((range) => Array.from(range.getClientRects())).filter((r) => r.width > 0 && r.height > 0)
  const first = rects.at(0)
  if (!first) return false
  const doc = layer.ownerDocument
  const vertical = isVerticalText(ranges, rects)
  const style = SKIM_LEVEL_STYLES[level]
  const toX = (x: number) => (x - origin.x) / origin.scale
  const toY = (y: number) => (y - origin.y) / origin.scale

  for (const run of joinRuns(rects, vertical)) {
    const geometry = vertical
      ? `left:${toX(run.right) + LINE_GAP}px;top:${toY(run.top)}px;width:${style.thickness}px;height:${(run.bottom - run.top) / origin.scale}px`
      : `left:${toX(run.left)}px;top:${toY(run.bottom) + LINE_GAP}px;width:${(run.right - run.left) / origin.scale}px;height:${style.thickness}px`
    layer.appendChild(createBox(doc, `${geometry};background:${style.color};border-radius:${style.thickness}px`))
  }

  if (style.badge) {
    // 文頭の手前（縦書きは上・横書きは左）に置き、まだ読んでいない側の文字にかからないようにする
    // 列・行の先頭から始まる文では手前に余白が無いことがあるので、描画範囲の外に出ないよう端で止める
    const left = Math.max(
      0,
      vertical ? toX(first.left + first.width / 2) - BADGE_SIZE / 2 : toX(first.left) - BADGE_SIZE - 1,
    )
    const top = Math.max(
      0,
      vertical ? toY(first.top) - BADGE_SIZE - 1 : toY(first.top + first.height / 2) - BADGE_SIZE / 2,
    )
    const badge = createBox(
      doc,
      `left:${left}px;top:${top}px;width:${BADGE_SIZE}px;height:${BADGE_SIZE}px;border-radius:50%;` +
        `background:${style.color};color:${style.badgeText};font:700 10px/${BADGE_SIZE}px sans-serif;text-align:center;` +
        'writing-mode:horizontal-tb;box-shadow:0 0 0 1px rgba(0,0,0,0.15)',
    )
    badge.textContent = style.badge
    layer.appendChild(badge)
  }
  return true
}

type Box = { left: number; top: number; right: number; bottom: number }

/** 矩形を行（縦書きは列）ごとにまとめる（ルビや span で 1 行が複数の矩形に分かれるため） */
const groupLines = (rects: Box[], vertical: boolean): Box[] => {
  const lines: Box[] = []
  for (const rect of rects) {
    const sameLine = (l: Box) =>
      vertical
        ? Math.abs((l.left + l.right) / 2 - (rect.left + rect.right) / 2) <
          Math.max(l.right - l.left, rect.right - rect.left) / 2
        : Math.abs((l.top + l.bottom) / 2 - (rect.top + rect.bottom) / 2) <
          Math.max(l.bottom - l.top, rect.bottom - rect.top) / 2
    const line = lines.find(sameLine)
    if (!line) {
      lines.push({ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom })
      continue
    }
    line.left = Math.min(line.left, rect.left)
    line.top = Math.min(line.top, rect.top)
    line.right = Math.max(line.right, rect.right)
    line.bottom = Math.max(line.bottom, rect.bottom)
  }
  return lines
}

/**
 * 隣り合う行（縦書きは列）を 1 本の帯にまとめる（行間で帯が途切れて点線に見えないように）
 * 行の太さより離れている行・行頭の位置が大きくずれる行（段組みの別の段など）は別の帯にする
 */
const mergeBands = (lines: Box[], vertical: boolean): Box[] => {
  const ordered = [...lines].sort((a, b) => (vertical ? b.right - a.right : a.top - b.top))
  const bands: Box[] = []
  for (const line of ordered) {
    const band = bands.at(-1)
    const size = vertical ? line.right - line.left : line.bottom - line.top
    const gap = band ? (vertical ? band.left - line.right : line.top - band.bottom) : Infinity
    const shift = band ? (vertical ? Math.abs(line.top - band.top) : Math.abs(line.left - band.left)) : Infinity
    if (!band || gap > size || shift > size * 2) {
      bands.push({ ...line })
      continue
    }
    band.left = Math.min(band.left, line.left)
    band.top = Math.min(band.top, line.top)
    band.right = Math.max(band.right, line.right)
    band.bottom = Math.max(band.bottom, line.bottom)
  }
  return bands
}

/** 本文の順で次の文字が、同じ行（縦書きは列）の続きとして隣にあるか */
const continuesSegment = (a: Rect, b: Rect): boolean => {
  const tall = (r: Rect) => r.height > r.width * VERTICAL_ASPECT
  const wide = (r: Rect) => r.width > r.height * VERTICAL_ASPECT
  const down = b.top - a.bottom
  const right = b.left - a.right
  const downward =
    !wide(a) &&
    !wide(b) &&
    Math.abs((a.left + a.right - b.left - b.right) / 2) < Math.max(a.width, b.width) / 2 &&
    down >= -Math.min(a.width, b.width) / 2 &&
    down <= Math.max(a.width, b.width)
  const rightward =
    !tall(a) &&
    !tall(b) &&
    Math.abs((a.top + a.bottom - b.top - b.bottom) / 2) < Math.max(a.height, b.height) / 2 &&
    right >= -Math.min(a.height, b.height) / 2 &&
    right <= Math.max(a.height, b.height)
  return downward || rightward
}

/** 1 行分の矩形の向き（1 文字だけで判断できないときは null） */
const segmentDirection = (rects: Rect[]): boolean | null => {
  const only = rects.length === 1 ? rects.at(0) : undefined
  if (!only) return isVerticalLayout(rects)
  if (only.height > only.width * VERTICAL_ASPECT) return true
  if (only.width > only.height * VERTICAL_ASPECT) return false
  return null
}

const toBox = (rects: Rect[]): Box => ({
  left: Math.min(...rects.map((r) => r.left)),
  top: Math.min(...rects.map((r) => r.top)),
  right: Math.max(...rects.map((r) => r.right)),
  bottom: Math.max(...rects.map((r) => r.bottom)),
})

/**
 * 本文の断片に帯を付ける範囲（縦書きは列、横書きは行のまとまり）を求める
 *
 * PDF の文字レイヤーでは、断片の途中にある図の中の文字（見出しやラベル）も本文として拾われる。
 * そこで 1 行ずつ向きを調べ、断片の大半を占める向き（本文の向き）と違う行（縦書きの本の図の横書き文字など）を外し、
 * まとめた後も文字数が少なく孤立した帯（図のラベルなど）は外す。
 * @param verticalHint writing-mode から縦書きと分かっているとき true
 */
export const passageBands = (rects: Rect[], verticalHint: boolean): { vertical: boolean; bands: Box[] } => {
  const segments: Rect[][] = []
  for (const rect of rects) {
    const segment = segments.at(-1)
    const last = segment?.at(-1)
    if (segment && last && continuesSegment(last, rect)) segment.push(rect)
    else segments.push([rect])
  }

  const lines = segments.map((segment) => ({ box: toBox(segment), vertical: segmentDirection(segment) }))
  let verticalChars = 0
  let horizontalChars = 0
  for (const { box, vertical } of lines) {
    const width = box.right - box.left
    const height = box.bottom - box.top
    if (vertical === true) verticalChars += height / width
    if (vertical === false) horizontalChars += width / height
  }
  const vertical = verticalHint || verticalChars > horizontalChars

  const kept: Box[] = []
  const others: Box[] = []
  for (const line of lines) (line.vertical === null || line.vertical === vertical ? kept : others).push(line.box)
  const grouped = groupLines(kept, vertical)
  const cross = (b: Box) => (vertical ? b.right - b.left : b.bottom - b.top)
  const flow = (b: Box) => (vertical ? b.bottom - b.top : b.right - b.left)
  const sizes = grouped.map(cross).sort((a, b) => a - b)
  const charSize = sizes.at(Math.floor(sizes.length / 2)) ?? 0
  // 向きの違う文字でも、本文の列（行）の中にあるもの（縦中横の数字など）はその列に戻し、
  // 列のすぐ隣にあるもの（PDF で段落末の「る。」だけが横向きに入っているなど）は 1 列として残す
  for (const box of others) {
    const inside = grouped.find((l) =>
      vertical
        ? (box.left + box.right) / 2 >= l.left &&
          (box.left + box.right) / 2 <= l.right &&
          box.top <= l.bottom + cross(l) &&
          box.bottom >= l.top - cross(l)
        : (box.top + box.bottom) / 2 >= l.top &&
          (box.top + box.bottom) / 2 <= l.bottom &&
          box.left <= l.right + cross(l) &&
          box.right >= l.left - cross(l),
    )
    if (inside) {
      inside.left = Math.min(inside.left, box.left)
      inside.top = Math.min(inside.top, box.top)
      inside.right = Math.max(inside.right, box.right)
      inside.bottom = Math.max(inside.bottom, box.bottom)
      continue
    }
    const beside = grouped.some((l) => {
      const distance = vertical
        ? Math.max(l.left - box.right, box.left - l.right, 0)
        : Math.max(l.top - box.bottom, box.top - l.bottom, 0)
      const overlaps = vertical
        ? l.top <= box.bottom && box.top <= l.bottom
        : l.left <= box.right && box.left <= l.right
      return overlaps && distance <= charSize
    })
    if (beside) grouped.push({ ...box })
  }

  const merged = mergeBands(grouped, vertical)
  const isLong = (b: Box) => flow(b) >= charSize * PASSAGE_MIN_BAND_CHARS
  // 短い帯でも、長い帯のすぐ隣にあるもの（段落の最後の短い列など）は本文なので残す
  const nextToLong = (b: Box) =>
    merged.some((o) => {
      if (o === b || !isLong(o)) return false
      const distance = vertical
        ? Math.max(o.left - b.right, b.left - o.right, 0)
        : Math.max(o.top - b.bottom, b.top - o.bottom, 0)
      const overlaps = vertical ? o.top <= b.bottom && b.top <= o.bottom : o.left <= b.right && b.left <= o.right
      return overlaps && distance <= charSize * 2
    })
  const bands = merged.filter((band) => isLong(band) || nextToLong(band))
  return { vertical, bands }
}

/** 本文の断片の行頭側の余白に帯を描く */
const drawPassage = (layer: HTMLElement, ranges: Range[], origin: Origin): boolean => {
  const rects = ranges.flatMap((range) => Array.from(range.getClientRects())).filter((r) => r.width > 0 && r.height > 0)
  if (rects.length === 0) return false
  const doc = layer.ownerDocument
  const { vertical, bands } = passageBands(rects, hasVerticalWritingMode(ranges))
  if (bands.length === 0) return false
  const toX = (x: number) => (x - origin.x) / origin.scale
  const toY = (y: number) => (y - origin.y) / origin.scale
  const offset = PURPOSE_BAR_GAP + PURPOSE_BAR_THICKNESS
  for (const line of bands) {
    const geometry = vertical
      ? `left:${toX(line.left)}px;top:${Math.max(0, toY(line.top) - offset)}px;width:${(line.right - line.left) / origin.scale}px;height:${PURPOSE_BAR_THICKNESS}px`
      : `left:${Math.max(0, toX(line.left) - offset)}px;top:${toY(line.top)}px;width:${PURPOSE_BAR_THICKNESS}px;height:${(line.bottom - line.top) / origin.scale}px`
    layer.appendChild(createBox(doc, `${geometry};background:${PURPOSE_MARK_COLOR};opacity:0.85`))
  }
  return true
}

const removeOverlay = (host: Element) => {
  for (const child of Array.from(host.children)) {
    if (child.classList.contains(OVERLAY_CLASS)) child.remove()
  }
}

/** root 配下の印（目的の帯・重要文の傍線と印）を、host の上の層に描き直す。描けた数を返す */
const renderInto = (host: HTMLElement, root: Node, marks: ReadingMarks, origin: Origin): number => {
  removeOverlay(host)
  const passages = marks.passages.length > 0 ? findPassageMatches(root, marks.passages) : []
  const matches = marks.sentences.length > 0 ? findSentenceMatches(root, marks.sentences) : []
  if (passages.length === 0 && matches.length === 0) return 0
  const layer = host.ownerDocument.createElement('div')
  layer.className = OVERLAY_CLASS
  layer.setAttribute('aria-hidden', 'true')
  layer.style.cssText =
    'position:absolute;left:0;top:0;width:0;height:0;overflow:visible;pointer-events:none;z-index:2147483000'
  let count = 0
  // 重要文の印（核・要）を上に重ねるため、目的の帯を先に描く
  for (const ranges of passages) {
    if (drawPassage(layer, ranges, origin)) count++
  }
  for (const { sentence, ranges } of matches) {
    if (drawSentence(layer, ranges, sentence.level, origin)) count++
  }
  host.appendChild(layer)
  return count
}

/** 描画先の要素の、画面上の位置と拡大率（PDF はズームで要素ごと拡大されることがある） */
const elementOrigin = (host: HTMLElement): Origin => {
  const rect = host.getBoundingClientRect()
  const scale = host.offsetWidth > 0 ? rect.width / host.offsetWidth : 1
  return { x: rect.left, y: rect.top, scale: scale || 1 }
}

const isOverlayNode = (node: Node) => node instanceof Element && node.classList.contains(OVERLAY_CLASS)

/** DOM の変化が傍線と印の描き直しだけによるものか（MutationObserver で描き直しが連鎖しないように使う） */
export const isSkimOverlayMutation = (records: MutationRecord[]): boolean =>
  records.every((record) => [...record.addedNodes, ...record.removedNodes].every(isOverlayNode))

// 本文の大きさが変わったら引き直すための監視（window ごと）
const watchers = new WeakMap<Window, () => void>()

const stopWatching = (wnd: Window) => {
  watchers.get(wnd)?.()
  watchers.delete(wnd)
}

/**
 * window の本文に流し読みの傍線と印・読む目的の帯を描く（前回の描画は置き換える）
 * @param roots 本文を探す範囲（PDF の各ページの文字レイヤー）。各要素の親（ページ）の上に描く。
 *   省略時は document.body から探し、ページ全体の上に描く（EPUB の iframe）
 * @returns 描けた文・断片の数（window が破棄済みなら null）
 */
export const applySkimHighlights = (wnd: Window, marks: ReadingMarks, roots?: Element[]): number | null => {
  let doc: Document
  try {
    doc = wnd.document
  } catch {
    // 破棄済みの iframe の window は触ると例外になる
    return null
  }

  if (roots) {
    let count = 0
    for (const root of roots) {
      const host = root.parentElement
      if (host) count += renderInto(host, root, marks, elementOrigin(host))
    }
    return count
  }

  const body = doc.body
  if (!body) return null
  // html 直下の層は文書の原点に置かれるので、スクロール量を足して文書の座標にする
  const render = () => renderInto(doc.documentElement, body, marks, { x: -wnd.scrollX, y: -wnd.scrollY, scale: 1 })
  const count = render()

  stopWatching(wnd)
  let timer: ReturnType<typeof setTimeout> | undefined
  const schedule = () => {
    clearTimeout(timer)
    timer = setTimeout(render, REDRAW_DELAY_MS)
  }
  const Observer = (wnd as Window & typeof globalThis).ResizeObserver ?? ResizeObserver
  const observer = new Observer(schedule)
  observer.observe(body)
  wnd.addEventListener('resize', schedule)
  watchers.set(wnd, () => {
    observer.disconnect()
    wnd.removeEventListener('resize', schedule)
    clearTimeout(timer)
  })
  return count
}

/** window の本文から流し読みの傍線と印を消す */
export const clearSkimHighlights = (wnd: Window) => {
  stopWatching(wnd)
  try {
    wnd.document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((el) => el.remove())
  } catch {
    // 破棄済みの iframe
  }
}
